'use client';

import React, { useCallback, useRef } from 'react';
import dynamic from 'next/dynamic';
import { useTranslation } from 'next-i18next/pages';
import { type PageTrackingTypes } from '@pkgs/components/types/tracking';
import { Breakpoints } from '@pkgs/components/providers/BreakpointProvider';
import { useBreakpoint } from '@pkgs/components/hooks/useBreakpoint';
import { SearchMode } from '@pkgs/components/hooks/stores/search/slices/searchModeSlice';
import { type TFunction } from '@pkgs/components';
import {
  type Currency,
  type GenericExposeListing,
  type Language,
  type MasterDataShopResponse,
  MeasurementSystem,
  replaceLocaleInPath,
  type SearchListingsFilters,
} from '@pkgs/api';
import { getCountryName, isValidCountryNameCode, type Units, units } from '@ev-tech/ui-core-components/utils';
import { useRouting } from '@ev-tech/ui-core-components/RoutingProvider';
import { useModalState } from '@ev-tech/ui-core-components/Modal';
import {
  type HandleFlyoutRef,
  Header,
  type StoryblokHeaderProps,
  type HeaderProps as UiCoreHeaderProps,
} from '@ev-tech/ui-core-components/Header';
import { mapBlokCountriesToSelectItems, type SbBaseBlockProps, type StoryblokDatasourceEntry } from '@ev-tech/ui-core-components/cms';
import { UserActionEnum } from '@ev-tech/snowplow-library';
import { type UseContactWizardArgs } from '@ev-tech/lead-flows';
import { datadogRum } from '@datadog/browser-rum';

import { type NavigationTrackingContext } from '@/types/tracking';
import { TRANSLATION_NAMESPACE } from '@/i18n/config';
import { useUserData } from '@/hooks/useUserData';
import { useSettings } from '@/hooks/useSettings';
import { usePageTrackingTypes } from '@/hooks/usePageTrackingTypes';
import { useExposeLanguageStore } from '@/hooks/useExposeLanguageStore';

import { mapLanguageAndName } from '../utils/mapLanguageName';
import { getLinkUrl } from '../utils/linkResolver';
import { buildMetaNavigation, buildNavigation } from '../utils/headerUtils';
import { trackNavigationCustomEvent, trackUserInteraction } from '../lib/tracking/snowplow';
import { ELEMENT_IDS } from '../constants';
import { UserAccount } from '../../user-account-modules-wrapper/UserAccount';

const SettingsModal = dynamic(() => import('@ev-tech/ui-core-components/SettingsModal').then((mod) => mod.SettingsModal));

const unitLabel = {
  SQMT: 'appSettingsModal.units.mt.label',
  SQFT: 'appSettingsModal.units.sqft.label',
} as const;

const contactTriggerElementIds = {
  meta: 'meta-header_secondary_contact',
  sticky: 'sticky-header_secondary_contact',
} as const;

type HeaderBlockProps = SbBaseBlockProps<StoryblokHeaderProps> & {
  className?: string;
  pageLanguages?: string[] | null;
  onLanguageChange?: (locale: string) => void;
  isSticky?: boolean;
  onContactCtaClick?: (args?: UseContactWizardArgs) => void;
  externalLinkDomains?: StoryblokDatasourceEntry[] | null;
  listing?: GenericExposeListing;
  pageTrackingTypes?: PageTrackingTypes;
  filters?: SearchListingsFilters;
  countryLinks?: StoryblokDatasourceEntry[] | null;
  isMapToggledOn?: boolean;
  searchMode?: SearchMode;
  shop?: MasterDataShopResponse;
};

type HeaderCallToAction = NonNullable<UiCoreHeaderProps['callToAction']> & {
  testId: string;
};

export const HeaderBlock = ({
  blok,
  className,
  pageLanguages,
  onLanguageChange,
  isSticky,
  onContactCtaClick,
  externalLinkDomains,
  listing,
  pageTrackingTypes: providedPageTrackingTypes,
  filters,
  countryLinks,
  isMapToggledOn,
  searchMode,
  shop,
}: HeaderBlockProps) => {
  const { t } = useTranslation(TRANSLATION_NAMESPACE.sp_header_footer);
  const { t: tCommon } = useTranslation(TRANSLATION_NAMESPACE.common);

  const { router } = useRouting();
  const settingsModalState = useModalState();
  const countryModalState = useModalState();
  const { currency, measurementSystem, countryCode, setSettings, language } = useSettings();
  const { breakpoint } = useBreakpoint();
  const { userData } = useUserData();
  const exposeLanguage = useExposeLanguageStore((state) => state.exposeLanguage);
  const inferredPageTrackingTypes = usePageTrackingTypes();
  const pageTrackingTypes = providedPageTrackingTypes ?? inferredPageTrackingTypes;

  const handleFlyoutRef = useRef<HandleFlyoutRef>(null);
  const hasEnteredSettingsButton = useRef(false);

  const unit = measurementSystem === MeasurementSystem.imperial ? 'SQFT' : 'SQMT';
  const languages = mapLanguageAndName(pageLanguages ?? [], t);
  const normalizedCountryCode = countryCode.toLowerCase();
  const currentCountryName = isValidCountryNameCode(normalizedCountryCode)
    ? getCountryName(normalizedCountryCode, t)
    : normalizedCountryCode;
  const logoUrl = `/${normalizedCountryCode}/${language}`;

  const settingsLabels = {
    languageSelectLabel: t('appSettingsModal.languageSelect.label'),
    languageSelectTooltipText: t('appSettingsModal.languageSelect.tooltipText'),
    currencySelectLabel: t('appSettingsModal.currencySelect.label'),
    unitsRadioLabel: t('appSettingsModal.unitsRadio.label'),
    currentCountry: t('appSettingsModal.currentCountry'),
    changeCountry: t('appSettingsModal.changeCountry'),
    applyButtonLabel: t('appSettingsModal.applyButton.label'),
    buttonBackLabel: t('header.navigation.button.back'),
    buttonCloseLabel: t('header.navigation.button.close'),
    settingsTitle: t('appSettingsModal.settingsTitle'),
    countriesListTestId: 'header-country-selection-list',
    countrySelectionOverline: t('appSettingsModal.countrySelection.overline'),
    countrySelectionHeadline: t('appSettingsModal.countrySelection.headline'),
    countrySelectionCopy: t('appSettingsModal.countrySelection.copy'),
    countrySelectionModalTitle: t('appSettingsModal.countrySelection.modalTitle'),
  };
  const parsedCountries = mapBlokCountriesToSelectItems(countryLinks, countryCode, t, (message, context) => {
    datadogRum.addError(new Error(message), context);
  });
  const isMinTabletLandscape = Boolean(breakpoint && breakpoint >= Breakpoints.TABLET_LANDSCAPE);

  const handleContactCtaClick = useCallback(() => {
    if (!onContactCtaClick) return;

    onContactCtaClick({
      triggerElementId: isSticky ? contactTriggerElementIds.sticky : contactTriggerElementIds.meta,
      ...(userData ? { prefillUserInfo: userData } : {}),
    });
  }, [isSticky, onContactCtaClick, userData]);

  const metaNavigationMouseEventClose = useCallback(
    (event: React.MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!isMinTabletLandscape || countryModalState.isOpen) {
        return;
      }

      // Fix to prevent FF from firing MouseLeave Event when pressing a Select
      if (target.nodeName.toLowerCase() === 'select') {
        return;
      }

      settingsModalState.close();

      trackUserInteraction({
        userInteractionData: {
          action: UserActionEnum.mouseLeave,
          elementId: 'meta-header_settings_close',
        },
        language,
        countryCode,
        searchMode,
        shop,
      });
    },
    [isMinTabletLandscape, countryModalState.isOpen, settingsModalState, language, countryCode, searchMode, shop],
  );

  const metaNavigationMouseEventOpen = useCallback(() => {
    if (!isMinTabletLandscape || countryModalState.isOpen || hasEnteredSettingsButton.current) {
      return;
    }

    hasEnteredSettingsButton.current = true;

    settingsModalState.open();
    trackUserInteraction({
      userInteractionData: {
        action: UserActionEnum.mouseEnter,
        elementId: 'meta-header_settings',
      },
      language,
      countryCode,
      searchMode,
      shop,
    });

    setTimeout(() => {
      hasEnteredSettingsButton.current = false;
    }, 100);
  }, [isMinTabletLandscape, countryModalState.isOpen, settingsModalState, language, countryCode, searchMode, shop]);

  const unitsArray = Object.keys(units).map((key, index) => {
    const unitKey = key as Units;
    return {
      id: `${index + 1}`,
      children: `${t(unitLabel[unitKey])}`,
      value: units[unitKey],
    };
  });

  const trackItem = useCallback(
    (navigationData: NavigationTrackingContext) =>
      trackNavigationCustomEvent({
        navigationData,
        language,
        countryCode,
        listing,
        shop,
        pageType: pageTrackingTypes?.pageType,
        templateType: pageTrackingTypes?.templateType,
        filters,
        isMapToggledOn,
        measurementSystem,
        searchMode: searchMode ?? SearchMode.CLASSIC,
      }),
    [language, countryCode, listing, shop, pageTrackingTypes, filters, isMapToggledOn, measurementSystem, searchMode],
  );

  const handleLanguageChange = useCallback(
    (targetLanguage: string) => {
      if (onLanguageChange) onLanguageChange(targetLanguage);
      const targetUrl = replaceLocaleInPath(location.pathname + location.search, targetLanguage as Language);
      router.push(targetUrl);
    },
    [onLanguageChange, router],
  );

  const handleCurrencyChange = useCallback(
    (currency: string) => {
      setSettings({ currency: currency as Currency });
    },
    [setSettings],
  );

  const handleMeasurementUnitChange = useCallback(
    (unit: string) => {
      setSettings({
        measurementSystem: unit === units.SQMT ? MeasurementSystem.metric : MeasurementSystem.imperial,
      });
    },
    [setSettings],
  );

  const handleSettingsClick = useCallback(() => {
    settingsModalState.open();
    trackUserInteraction({
      userInteractionData: {
        action: UserActionEnum.click,
        elementId: 'meta-header_settings',
      },
      language,
      countryCode,
      shop,
    });
  }, [settingsModalState, language, countryCode, shop]);

  const handleTeaserLinkClick = useCallback(
    (itemIndex: number, item: NonNullable<(typeof blok.items)[0]>) => {
      const teaserElement = item.teaserElement?.[0];
      if (!teaserElement) return;

      trackItem({
        elementId: `${ELEMENT_IDS.menuHeader.title}-${itemIndex + 1}_${ELEMENT_IDS.menuHeader.teaser}`,
        href: getLinkUrl(teaserElement.link),
      });
      return undefined;
    },
    [trackItem],
  );

  const handleNavigationClose = useCallback(
    () =>
      trackUserInteraction({
        userInteractionData: {
          action: UserActionEnum.click,
          elementId: 'meta-header_settings_close',
        },
        language,
        countryCode,
        shop,
      }),
    [language, countryCode, shop],
  );

  const handleCountrySelect = useCallback(
    (href: string, selectedCountryCode: string) =>
      trackItem({
        elementId: 'country-list_link',
        href,
        selectedOption: selectedCountryCode,
        sourceScreen: 'meta-header_select-country',
      }),
    [trackItem],
  );

  const handleSettingsClose = useCallback(
    () =>
      trackUserInteraction({
        userInteractionData: {
          action: UserActionEnum.click,
          elementId: 'meta-header_settings_country_close',
        },
        language,
        countryCode,
        shop,
      }),
    [language, countryCode, shop],
  );

  const handleApplyButton = useCallback(
    (e: React.MouseEvent) => {
      settingsModalState.close();
      handleFlyoutRef.current?.closeNavigation();
      e.stopPropagation();
    },
    [settingsModalState],
  );

  const metaNavigation = buildMetaNavigation({
    blok,
    countryCode,
    language,
    currency,
    unit,
    settingsLabels,
    settingsModalState,
    countryModalState,
    parsedCountries,
    languages,
    currentCountryName,
    locationsLabel: t('header.locations.label'),
    unitsArray,
    settingsModalComponent: SettingsModal,
    onMouseEnter: metaNavigationMouseEventOpen,
    onMouseLeave: metaNavigationMouseEventClose,
    onSettingsClick: handleSettingsClick,
    onNavigationClose: handleNavigationClose,
    onLanguageChange: handleLanguageChange,
    onCurrencyChange: handleCurrencyChange,
    onUnitChange: handleMeasurementUnitChange,
    onCountryChange: handleCountrySelect,
    onCountryModalClose: handleSettingsClose,
    onApplyButton: handleApplyButton,
    trackItem,
  });

  const parsedNavigation = buildNavigation({
    blok,
    externalLinkDomains,
    t,
    tCommon: tCommon as TFunction,
    trackItem,
    onTeaserLinkClick: handleTeaserLinkClick,
  });

  const handleLogoClick = useCallback(
    () =>
      trackItem({
        elementId: 'meta-header_logo',
        href: logoUrl,
        selectedOption: exposeLanguage,
      }),
    [trackItem, logoUrl, exposeLanguage],
  );

  const handleOnSignInFlyoutOpen = useCallback(() => handleFlyoutRef.current?.closeNavigation(), [handleFlyoutRef]);
  const contactCallToAction: HeaderCallToAction | undefined = onContactCtaClick
    ? {
        label: blok.get_in_touch_label,
        onClick: handleContactCtaClick,
        variant: 'secondary',
        testId: 'header-contact-button',
      }
    : undefined;

  return (
    <Header
      ref={handleFlyoutRef}
      className={className}
      testId="sp-header"
      logo={{
        href: logoUrl,
        onClick: handleLogoClick,
      }}
      navigation={parsedNavigation}
      metaNavigation={metaNavigation}
      userAccount={
        <UserAccount
          countryCode={countryCode}
          language={language}
          onSignInFlyoutOpen={handleOnSignInFlyoutOpen}
          pageTrackingTypes={pageTrackingTypes}
          shopId={shop?.id}
        />
      }
      callToAction={contactCallToAction}
      sticky={isSticky}
      variant={blok.privateOffice ? 'private-office' : undefined}
    />
  );
};
